// The Usage demo's reports as complete HTML documents, generated
// with il-report from plans/usage-demo-reports/*.layout.json by
// scripts/build-usage-demo-reports.py. Each carries its own styles, so the
// project page renders it in an iframe. Generated; do not edit.

export const OPPORTUNITY_MICROSOFT = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-positive wide"><div class="label label-positive">Recommendation</div><div class="report-tag report-tag-positive">Renegotiate before the {{DS+62}} notice</div><div class="card-title-container"><div class="card-title"><span class="truncate">Microsoft Enterprise Agreement</span></div><div class="card-subtitle"><span class="card-subtitle">Opportunity report · Renewal · Notice by {{D+62}}, term ends {{D+152}}</span></div></div><div class="card-description"><p>The renewal carries a large, well-evidenced savings opportunity. <strong>More than a quarter of the E5 seats paid for show no activity in 90 days</strong> and the unit price sits above the peer median. Letting the agreement renew as is locks the current quantity and price in for three more years, with no right to reduce and a 5% list rise announced for {{MON+153}}.</p></div></div><div class="report-summary-card report-summary-card-positive narrow"><div class="label label-positive">Opportunity at a glance</div><div class="report-tag report-tag-positive">High opportunity</div><div class="card-title-container"><div class="card-title"><span class="truncate">$2.4M to $3.4M</span></div><div class="card-subtitle"><span class="card-subtitle">Potential annual savings</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Share of annual value</span><span class="card-data-row-value">28% to 40%</span></div><div class="card-data-row"><span class="card-data-row-label">Annual contract value</span><span class="card-data-row-value">$8.5M</span></div><div class="card-data-row"><span class="card-data-row-label">Contract term</span><span class="card-data-row-value">{{D-942}} to {{D+152}}</span></div></div></div><div class="report-summary-card report-summary-card-warning narrow"><div class="label label-warning">Priority</div><div class="report-tag report-tag-warning">High priority</div><div class="card-title-container"><div class="card-title"><span class="truncate">Act before the {{DS+62}} notice</span></div><div class="card-subtitle"><span class="card-subtitle">Large upside, a hard notice date and a contract that forbids reducing later</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Financial opportunity</span><span class="card-data-row-value">Strong</span></div><div class="card-data-row"><span class="card-data-row-label">Negotiation leverage</span><span class="card-data-row-value">Moderate</span></div><div class="card-data-row"><span class="card-data-row-label">Decision urgency</span><span class="card-data-row-value">High</span></div><div class="card-data-row"><span class="card-data-row-label">Contractual risk</span><span class="card-data-row-value">High</span></div></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="report">Opportunity report</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="report"><button class="report-sidenav-btn" data-target="about"><span>About the deal</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="glance"><span>Opportunity at a glance</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="where"><span>Where the opportunity is</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="findings"><span>Key findings</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="who"><span>Where the idle seats sit, and who owns them</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="assessment"><span>Assessment and recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="report"><div class="report-section" id="about"><div class="report-section-subheader"><p class="report-section-title">About the deal</p><p class="report-section-description">Prepared {{D-1}} from the Microsoft 365 usage export, Entra sign-ins and Workday; an estimate before the function owners confirm, not a playbook.</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 28%"><col></colgroup><thead><tr><th><span>Item</span></th><th><span>Detail</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Supplier</span></td><td><span>Microsoft</span></td></tr><tr><td><span class="report-table-position">Agreement</span></td><td><span>Enterprise Agreement, Microsoft 365 E5 and add-ons</span></td></tr><tr><td><span class="report-table-position">Deal type</span></td><td><span>Renewal, year 3 of 3</span></td></tr><tr><td><span class="report-table-position">Annual contract value</span></td><td><span>$8.5M, plus $0.6M of premier support under a separate agreement</span></td></tr><tr><td><span class="report-table-position">Licenses</span></td><td><span>12,400 E5 at $57.00 a user a month ($684 a year)</span></td></tr><tr><td><span class="report-table-position">Term</span></td><td><span>{{D-942}} to {{D+152}}</span></td></tr><tr><td><span class="report-table-position">Notice date</span></td><td><span>{{D+62}}; 90 days before the term ends</span></td></tr><tr><td><span class="report-table-position">Negotiation owner</span></td><td><span>Rachel Sato</span></td></tr></tbody></table></div></div><div class="report-section" id="glance"><div class="report-section-subheader-row"><p class="report-section-title">Opportunity at a glance</p><p class="report-section-stat positive">$2.4M to $3.4M · 28% to 40%</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Potential savings</span><span class="report-stat-grid-value positive">$2.4M to $3.4M</span><span class="report-stat-grid-sub">Per year</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Savings opportunity</span><span class="report-stat-grid-value">28% to 40%</span><span class="report-stat-grid-sub">Of annual value</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Seats without activity</span><span class="report-stat-grid-value warning">3,500</span><span class="report-stat-grid-sub">Of 12,400 in the last 90 days</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Opportunity level</span><span class="report-stat-grid-value">High</span><span class="report-stat-grid-sub">Quantity, edition and price</span></div></div></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">Where the estimate comes from</p><table class="report-table"><colgroup><col><col style="width: 20%"><col></colgroup><thead><tr><th><span>Lever</span></th><th class="report-table-cell-numeric"><span>Estimate</span></th><th><span>Basis</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Right-size the quantity and edition</span></td><td class="report-table-cell-numeric"><span>$1.5M to $2.3M</span></td><td><span>3,500 seats inactive, departed or never assigned at $684 a year; how many release, and how many fit E3, is for the function owners to confirm</span></td></tr><tr><td><span class="report-table-position">Price at the peer median or better</span></td><td class="report-table-cell-numeric"><span>$0.9M to $1.1M</span></td><td><span>23 comparable agreements, median $49.50, target $48.00</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span class="positive">$2.4M to $3.4M</span></td><td><span>Before the vendor&apos;s response; midpoint $2.9M</span></td></tr></tbody></table></div></div></div><div class="report-section" id="where"><div class="report-section-subheader"><p class="report-section-title">Where the opportunity is</p><p class="report-section-description">The gap between what is bought and what is used, what it is made of, and what it is worth a year.</p></div><div class="report-section-card-container"><div class="report-vega-chart"><p class="report-table-title">Active users against licenses bought, by month; dashed: projected, with the recommended quantity from the renewal</p><div class="report-vega-chart-svg"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" class="marks" width="950" height="315" viewBox="0 0 950 315"><g fill="none" stroke-miterlimit="4" transform="translate(81,37)"><g class="mark-group role-frame root" role="graphics-object" aria-roledescription="group mark container"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0.5,0.5h860v240h-860Z"/><g><g class="mark-group role-axis" aria-hidden="true"><g transform="translate(0.5,240.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-grid" pointer-events="none"><line transform="translate(0,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(43,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(86,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(129,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(172,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(215,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(258,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(301,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(344,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(387,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(430,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(473,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(516,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(559,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(602,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(645,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(688,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(731,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(774,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(817,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(860,-240)" x2="0" y2="240" stroke="#F0F0F0" stroke-width="1" opacity="1"/></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-axis" aria-hidden="true"><g transform="translate(0.5,0.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-grid" pointer-events="none"><line transform="translate(0,240)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,192)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,144)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,96)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,48)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,0)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-axis" role="graphics-symbol" aria-roledescription="axis" aria-label="X-axis titled 'Months to the renewal (0 is the renewal month)' for a linear scale with values from −18 to 2"><g transform="translate(0.5,240.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-tick" pointer-events="none"><line transform="translate(0,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(43,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(86,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(129,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(172,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(215,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(258,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(301,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(344,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(387,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(430,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(473,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(516,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(559,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(602,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(645,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(688,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(731,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(774,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(817,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(860,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-label" pointer-events="none"><text text-anchor="start" transform="translate(0,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−18</text><text text-anchor="middle" transform="translate(43,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−17</text><text text-anchor="middle" transform="translate(86,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−16</text><text text-anchor="middle" transform="translate(129,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−15</text><text text-anchor="middle" transform="translate(172,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−14</text><text text-anchor="middle" transform="translate(215,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−13</text><text text-anchor="middle" transform="translate(258,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−12</text><text text-anchor="middle" transform="translate(301,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−11</text><text text-anchor="middle" transform="translate(344,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−10</text><text text-anchor="middle" transform="translate(387,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−9</text><text text-anchor="middle" transform="translate(430,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−8</text><text text-anchor="middle" transform="translate(473.00000000000006,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−7</text><text text-anchor="middle" transform="translate(516,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−6</text><text text-anchor="middle" transform="translate(559,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−5</text><text text-anchor="middle" transform="translate(602,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−4</text><text text-anchor="middle" transform="translate(645,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−3</text><text text-anchor="middle" transform="translate(688,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−2</text><text text-anchor="middle" transform="translate(731,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">−1</text><text text-anchor="middle" transform="translate(774,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">0</text><text text-anchor="middle" transform="translate(817,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">1</text><text text-anchor="end" transform="translate(860,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">2</text></g><g class="mark-rule role-axis-domain" pointer-events="none"><line transform="translate(0,0)" x2="860" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-title" pointer-events="none"><text text-anchor="middle" transform="translate(430,31)" font-family="Inter, sans-serif" font-size="12px" font-weight="600" fill="#404040" opacity="1">Months to the renewal (0 is the renewal month)</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-axis" role="graphics-symbol" aria-roledescription="axis" aria-label="Y-axis titled 'Licenses' for a linear scale with values from 8,000 to 13,000"><g transform="translate(0.5,0.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-tick" pointer-events="none"><line transform="translate(0,240)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,192)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,144)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,96)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,48)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,0)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-label" pointer-events="none"><text text-anchor="end" transform="translate(-7,243)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">8,000</text><text text-anchor="end" transform="translate(-7,195)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">9,000</text><text text-anchor="end" transform="translate(-7,147)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">10,000</text><text text-anchor="end" transform="translate(-7,99)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">11,000</text><text text-anchor="end" transform="translate(-7,50.999999999999986)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">12,000</text><text text-anchor="end" transform="translate(-7,3)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">13,000</text></g><g class="mark-rule role-axis-domain" pointer-events="none"><line transform="translate(0,240)" x2="0" y2="-240" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-title" pointer-events="none"><text text-anchor="middle" transform="translate(-63,120) rotate(-90) translate(0,-3)" font-family="Inter, sans-serif" font-size="12px" font-weight="600" fill="#404040" opacity="1">Licenses</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-scope layer_0_pathgroup" role="graphics-object" aria-roledescription="group mark container"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h860v240h-860Z"/><g><g class="mark-line role-mark layer_0_marks" role="graphics-object" aria-roledescription="line mark container"><path aria-label="m: −17; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="line mark" d="M43,28.8C57.333,28.8,71.667,28.8,86,28.8C100.333,28.8,114.667,28.8,129,28.8C143.333,28.8,157.667,28.8,172,28.8C186.333,28.8,200.667,28.8,215,28.8C229.333,28.8,243.667,28.8,258,28.8C272.333,28.8,286.667,28.8,301,28.8C315.333,28.8,329.667,28.8,344,28.8C358.333,28.8,372.667,28.8,387,28.8C401.333,28.8,415.667,28.8,430,28.8C444.333,28.8,458.667,28.8,473,28.8C487.333,28.8,501.667,28.8,516,28.8" stroke="#2FB801" stroke-width="2" stroke-dasharray="1,0"/></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h860v240h-860Z"/><g><g class="mark-line role-mark layer_0_marks" role="graphics-object" aria-roledescription="line mark container"><path aria-label="m: −17; value: 9,620; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="line mark" d="M43,162.24C57.333,162.88,71.667,163.52,86,164.16C100.333,164.8,114.667,165.36,129,166.08C143.333,166.8,157.667,167.52,172,168.48C186.333,169.44,200.667,170.96,215,171.84C229.333,172.72,243.667,172.88,258,173.76C272.333,174.64,286.667,176,301,177.12C315.333,178.24,329.667,179.2,344,180.48C358.333,181.76,372.667,183.52,387,184.8C401.333,186.08,415.667,186.88,430,188.16C444.333,189.44,458.667,191.04,473,192.48C487.333,193.92,501.667,195.36,516,196.8" stroke="#006ACC" stroke-width="2" stroke-dasharray="1,0"/></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h860v240h-860Z"/><g><g class="mark-line role-mark layer_0_marks" role="graphics-object" aria-roledescription="line mark container"><path aria-label="m: −6; value: 12,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="line mark" d="M516,28.8C530.333,28.8,544.667,28.8,559,28.8C573.333,28.8,587.667,28.8,602,28.8C616.333,28.8,630.667,28.8,645,28.8C659.333,28.8,673.667,28.8,688,28.8C702.333,28.8,716.667,28.8,731,28.8C745.333,28.8,759.667,124.8,774,124.8C788.333,124.8,802.667,124.8,817,124.8C831.333,124.8,845.667,124.8,860,124.8" stroke="#2FB801" stroke-width="2" stroke-dasharray="6,4"/></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h860v240h-860Z"/><g><g class="mark-line role-mark layer_0_marks" role="graphics-object" aria-roledescription="line mark container"><path aria-label="m: −6; value: 8,900; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="line mark" d="M516,196.8C530.333,197.08,544.667,197.36,559,197.76C573.333,198.16,587.667,198.8,602,199.2C616.333,199.6,630.667,199.84,645,200.16C659.333,200.48,673.667,200.88,688,201.12C702.333,201.36,716.667,201.6,731,201.6C745.333,201.6,759.667,186.4,774,184.8C788.333,183.2,802.667,183.2,817,182.4C831.333,181.6,845.667,180.8,860,180" stroke="#006ACC" stroke-width="2" stroke-dasharray="6,4"/></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g></g><g class="mark-symbol role-mark layer_1_marks" role="graphics-object" aria-roledescription="symbol mark container"><path aria-label="m: −17; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(43,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −17; value: 9,620; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(43,162.23999999999998)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −16; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(86,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −16; value: 9,580; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(86,164.16)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −15; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(129,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −15; value: 9,540; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(129,166.07999999999998)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −14; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(172,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −14; value: 9,490; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(172,168.48)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −13; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(215,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −13; value: 9,420; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(215,171.84)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −12; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(258,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −12; value: 9,380; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(258,173.76)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −11; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(301,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −11; value: 9,310; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(301,177.12)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −10; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(344,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −10; value: 9,240; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(344,180.48)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −9; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(387,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −9; value: 9,150; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(387,184.8)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −8; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(430,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −8; value: 9,080; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(430,188.16)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −7; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(473.00000000000006,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −7; value: 8,990; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(473.00000000000006,192.48000000000002)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −6; value: 12,400; series: Licenses bought; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(516,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −6; value: 8,900; series: Active in 90 days; projected: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(516,196.8)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="m: −5; value: 12,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(559,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −5; value: 8,880; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(559,197.76000000000002)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −4; value: 12,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(602,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −4; value: 8,850; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(602,199.2)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −3; value: 12,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(645,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −3; value: 8,830; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(645,200.16)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −2; value: 12,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(688,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −2; value: 8,810; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(688,201.12)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −1; value: 12,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(731,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −1; value: 8,800; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(731,201.6)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: 0; value: 10,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(774,124.80000000000001)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: 0; value: 9,150; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(774,184.8)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: 1; value: 10,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(817,124.80000000000001)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: 1; value: 9,200; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(817,182.4)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: 2; value: 10,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(860,124.80000000000001)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: 2; value: 9,250; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(860,180)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −6; value: 12,400; series: Licenses bought; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(516,28.799999999999997)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="m: −6; value: 8,900; series: Active in 90 days; projected: projected" role="graphics-symbol" aria-roledescription="point" transform="translate(516,196.8)" d="M2.646,0A2.646,2.646,0,1,1,-2.646,0A2.646,2.646,0,1,1,2.646,0" fill="#006ACC" stroke-width="2" stroke-dasharray="6,4" opacity="1"/></g><g class="mark-group role-legend" role="graphics-symbol" aria-roledescription="legend" aria-label="Symbol legend for fill color and stroke color with 2 values: Active in 90 days, Licenses bought"><g transform="translate(0,-32)"><path class="background" aria-hidden="true" d="M0,0h325v14h-325Z" pointer-events="none"/><g><g class="mark-group role-legend-entry"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-group role-scope" role="graphics-object" aria-roledescription="group mark container"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h165v14h-165Z" pointer-events="none" opacity="1"/><g><g class="mark-symbol role-legend-symbol" pointer-events="none"><path transform="translate(6,6)" d="M-5,-5h10v10h-10Z" fill="#006ACC" stroke="#006ACC" stroke-width="1.5" opacity="1"/></g><g class="mark-text role-legend-label" pointer-events="none"><text text-anchor="start" transform="translate(16,9)" font-family="Inter, sans-serif" font-size="11px" fill="#404040" opacity="1">Active in 90 days</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g><g transform="translate(177,0)"><path class="background" aria-hidden="true" d="M0,0h148v14h-148Z" pointer-events="none" opacity="1"/><g><g class="mark-symbol role-legend-symbol" pointer-events="none"><path transform="translate(6,6)" d="M-5,-5h10v10h-10Z" fill="#2FB801" stroke="#2FB801" stroke-width="1.5" opacity="1"/></g><g class="mark-text role-legend-label" pointer-events="none"><text text-anchor="start" transform="translate(16,9)" font-family="Inter, sans-serif" font-size="11px" fill="#404040" opacity="1">Licenses bought</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g></g></g></svg></div></div></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">From licenses bought to licenses needed</p><table class="report-table"><colgroup><col><col style="width: 16%"><col></colgroup><thead><tr><th><span>Step</span></th><th class="report-table-cell-numeric"><span>Licenses</span></th><th><span>Basis</span></th></tr></thead><tbody><tr class="report-table-row-total"><td><span class="report-table-position">Licenses bought</span></td><td class="report-table-cell-numeric"><span>12,400</span></td><td><span>The EA&apos;s E5 quantity today</span></td></tr><tr><td><span class="report-table-position">Never assigned</span></td><td class="report-table-cell-numeric"><span>−550</span></td><td><span>Bought and never given to anyone</span></td></tr><tr><td><span class="report-table-position">Held by departed staff</span></td><td class="report-table-cell-numeric"><span>−560</span></td><td><span>Workday lists the holder as left</span></td></tr><tr><td><span class="report-table-position">Assigned, no sign-in in 90 days</span></td><td class="report-table-cell-numeric"><span class="warning">−2,390</span></td><td><span>Current staff; the function owners confirm how many release</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Active today</span></td><td class="report-table-cell-numeric"><span>8,900</span></td><td><span>Signed in within the last 90 days</span></td></tr><tr><td><span class="report-table-position">Headroom for joiners and seasonal staff</span></td><td class="report-table-cell-numeric"><span>+1,500</span></td><td><span>Last year&apos;s joiners and the seasonal peak, at the current rate</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Recommended quantity</span></td><td class="report-table-cell-numeric"><span class="positive">10,400</span></td><td><span>8,300 E5 and 2,100 E3</span></td></tr></tbody></table></div></div><div class="report-section-card-container"><div class="report-waterfall-wrap"><svg class="report-waterfall" width="980" height="340" viewBox="0 0 980 340" xmlns="http://www.w3.org/2000/svg"><line x1="80" x2="955" y1="285" y2="285" class="report-waterfall-baseline"></line><text x="70" y="289" text-anchor="end" class="report-waterfall-tick">$0M</text><line x1="80" x2="955" y1="225" y2="225" class="report-waterfall-gridline"></line><text x="70" y="229" text-anchor="end" class="report-waterfall-tick">$2.5M</text><line x1="80" x2="955" y1="165" y2="165" class="report-waterfall-gridline"></line><text x="70" y="169" text-anchor="end" class="report-waterfall-tick">$5M</text><line x1="80" x2="955" y1="105" y2="105" class="report-waterfall-gridline"></line><text x="70" y="109" text-anchor="end" class="report-waterfall-tick">$7.5M</text><line x1="80" x2="955" y1="45" y2="45" class="report-waterfall-gridline"></line><text x="70" y="49" text-anchor="end" class="report-waterfall-tick">$10M</text><rect x="122.5" y="81" width="90" height="204" rx="4" class="report-waterfall-bar report-waterfall-bar-total"></rect><text x="167.5" y="72" text-anchor="middle" class="report-waterfall-value report-waterfall-value-total">$8,500,000</text><text x="167.5" y="303" text-anchor="middle" class="report-waterfall-label">Annual cost today</text><line x1="212.5" x2="297.5" y1="81" y2="81" class="report-waterfall-connector"></line><rect x="297.5" y="81" width="90" height="32.88000000000001" rx="4" class="report-waterfall-bar report-waterfall-bar-captured-saving"></rect><text x="342.5" y="72" text-anchor="middle" class="report-waterfall-value report-waterfall-value-captured-saving">−$1,370,000</text><text x="342.5" y="303" text-anchor="middle" class="report-waterfall-label">2,000 fewer licenses</text><line x1="387.5" x2="472.5" y1="113.88000000000001" y2="113.88000000000001" class="report-waterfall-connector"></line><rect x="472.5" y="113.88000000000001" width="90" height="12.719999999999985" rx="4" class="report-waterfall-bar report-waterfall-bar-captured-saving"></rect><text x="517.5" y="104.88000000000001" text-anchor="middle" class="report-waterfall-value report-waterfall-value-captured-saving">−$530,000</text><text x="517.5" y="303" text-anchor="middle" class="report-waterfall-label">2,100 users on E3</text><line x1="562.5" x2="647.5" y1="126.6" y2="126.6" class="report-waterfall-connector"></line><rect x="647.5" y="126.6" width="90" height="24" rx="4" class="report-waterfall-bar report-waterfall-bar-avoided-exposure"></rect><text x="692.5" y="117.6" text-anchor="middle" class="report-waterfall-value report-waterfall-value-avoided-exposure">−$1,000,000</text><text x="692.5" y="303" text-anchor="middle" class="report-waterfall-label">Price at the peer target</text><line x1="737.5" x2="822.5" y1="150.6" y2="150.6" class="report-waterfall-connector"></line><rect x="822.5" y="150.6" width="90" height="134.4" rx="4" class="report-waterfall-bar report-waterfall-bar-total"></rect><text x="867.5" y="141.6" text-anchor="middle" class="report-waterfall-value report-waterfall-value-total">$5,600,000</text><text x="867.5" y="303" text-anchor="middle" class="report-waterfall-label">Target annual cost</text></svg><div class="report-waterfall-legend"><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-total"></span>Total</span><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-captured-saving"></span>Captured Saving</span><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-avoided-exposure"></span>Avoided Exposure</span></div></div></div><div class="report-section-card-container"><p class="report-text">The quantity and edition levers ($1.9M) come from the usage data and the owners&apos; confirmations; the price lever ($1.0M) is what the negotiation wins on top. The snapshot&apos;s $2.4M to $3.4M range brackets the $2.9M midpoint shown.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">By SKU: what is bought, used and at risk</p><table class="report-table"><colgroup><col><col style="width: 9%"><col style="width: 11%"><col style="width: 9%"><col style="width: 9%"><col style="width: 13%"><col style="width: 12%"><col style="width: 11%"></colgroup><thead><tr><th><span>SKU</span></th><th class="report-table-cell-numeric"><span>Bought</span></th><th class="report-table-cell-numeric"><span>Active 90 days</span></th><th class="report-table-cell-numeric"><span>Inactive</span></th><th class="report-table-cell-numeric"><span>Departed</span></th><th class="report-table-cell-numeric"><span>Fit a lower edition</span></th><th class="report-table-cell-numeric"><span>Unit price a year</span></th><th class="report-table-cell-numeric"><span>Cost at risk</span></th></tr></thead><tbody><tr class="report-table-row-focus"><td><span class="report-table-position">Microsoft 365 E5</span></td><td class="report-table-cell-numeric"><span>12,400</span></td><td class="report-table-cell-numeric"><span>8,900 (72%)</span></td><td class="report-table-cell-numeric"><span class="warning">2,390</span></td><td class="report-table-cell-numeric"><span>560</span></td><td class="report-table-cell-numeric"><span>2,100 to E3</span></td><td class="report-table-cell-numeric"><span>$684</span></td><td class="report-table-cell-numeric"><span class="negative">$2.39M</span></td></tr><tr><td><span class="report-table-position">Microsoft 365 Copilot</span></td><td class="report-table-cell-numeric"><span>1,000</span></td><td class="report-table-cell-numeric"><span>540 (54%)</span></td><td class="report-table-cell-numeric"><span class="warning">400</span></td><td class="report-table-cell-numeric"><span>30</span></td><td class="report-table-cell-numeric"><span>none</span></td><td class="report-table-cell-numeric"><span>$360</span></td><td class="report-table-cell-numeric"><span>$0.18M</span></td></tr><tr><td><span class="report-table-position">Visio Plan 2</span></td><td class="report-table-cell-numeric"><span>600</span></td><td class="report-table-cell-numeric"><span>310 (52%)</span></td><td class="report-table-cell-numeric"><span>270</span></td><td class="report-table-cell-numeric"><span>20</span></td><td class="report-table-cell-numeric"><span>120 to Plan 1</span></td><td class="report-table-cell-numeric"><span>$180</span></td><td class="report-table-cell-numeric"><span>$0.06M</span></td></tr><tr><td><span class="report-table-position">Project Plan 3</span></td><td class="report-table-cell-numeric"><span>400</span></td><td class="report-table-cell-numeric"><span>180 (45%)</span></td><td class="report-table-cell-numeric"><span>210</span></td><td class="report-table-cell-numeric"><span>10</span></td><td class="report-table-cell-numeric"><span>none</span></td><td class="report-table-cell-numeric"><span>$360</span></td><td class="report-table-cell-numeric"><span>$0.08M</span></td></tr><tr><td><span class="report-table-position">Power BI Premium per user</span></td><td class="report-table-cell-numeric"><span>800</span></td><td class="report-table-cell-numeric"><span>610 (76%)</span></td><td class="report-table-cell-numeric"><span>150</span></td><td class="report-table-cell-numeric"><span>15</span></td><td class="report-table-cell-numeric"><span>none</span></td><td class="report-table-cell-numeric"><span>$240</span></td><td class="report-table-cell-numeric"><span>$0.05M</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>15,200</span></td><td class="report-table-cell-numeric"><span>10,540</span></td><td class="report-table-cell-numeric"><span>3,420</span></td><td class="report-table-cell-numeric"><span>635</span></td><td class="report-table-cell-numeric"><span></span></td><td class="report-table-cell-numeric"><span></span></td><td class="report-table-cell-numeric"><span class="negative">$2.76M</span></td></tr></tbody></table></div></div></div><div class="report-section" id="findings"><div class="report-section-subheader-row"><p class="report-section-title">Key findings</p></div><div class="report-section-card-container"><div class="report-card-grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));"><div class="report-card"><div class="card-title-row"><span class="card-title">Usage and right-sizing</span><span class="card-title-stat">Strong opportunity</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">More than a quarter of the seats show no activity:</span></div><div class="card-description"><p>The Microsoft 365 usage export of {{DS-5}} shows 8,900 of 12,400 E5 licenses active in the last 90 days. The 3,500 others are assigned to current staff with no sign-in (2,390), held by departed staff (560), or never assigned (550).</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">About 1,100 seats are not in anyone&apos;s hands:</span></div><div class="card-description"><p>550 licenses were never assigned and 560 belong to people Workday lists as departed. These release without a conversation with the business.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">About 2,100 active users fit E3:</span></div><div class="card-description"><p>E5 costs $21 a month more than E3. 2,100 active users touched none of the E5-only features in 90 days; their owners confirm who moves.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Pricing and commercials</span><span class="card-title-stat">Above market</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">The E5 unit price is above the peer median:</span></div><div class="card-description"><p>Coupa shows $57.00 a user a month. The median of 23 comparable agreements is $49.50 and the best in the set sits at $46.00; the target is $48.00.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">A list price rise is announced for {{MON+153}}:</span></div><div class="card-description"><p>Microsoft has announced a 5% list increase from {{D+153}}, the day after the term ends. Renewing without a negotiated price inherits it.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Add-ons ride on the same renewal:</span></div><div class="card-description"><p>$0.6M of premier support renews on the same date under a separate agreement and should be priced with the EA, not after it.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Negotiation leverage</span><span class="card-title-stat">Moderate</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">The quantity ask is backed by data:</span></div><div class="card-description"><p>Seat-level activity from the tenant and headcount from Workday make the reduction a matter of record, not of opinion. Microsoft&apos;s own usage reports show the same picture.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">The alternative path is MCA-E, not a change of vendor:</span></div><div class="card-description"><p>Moving the workloads off Microsoft 365 is not credible in the window. The lever is the licensing vehicle and the quantity, and Microsoft prefers an EA renewal to a shrinking MCA-E.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Fiscal timing helps:</span></div><div class="card-description"><p>The notice date and the term&apos;s end both fall inside Microsoft&apos;s fiscal year, when field teams carry quota pressure on renewals.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Contract structure and risk</span><span class="card-title-stat">Requires attention</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">No right to reduce during the term:</span></div><div class="card-description"><p>The agreement allows true-ups only. Any seat kept at the renewal is paid for three years, whatever its use.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">No price protection:</span></div><div class="card-description"><p>Nothing caps the renewal price or the increase at the next anniversary. The announced 5% list rise applies unless the renewal fixes a price.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Supporting terms are unverified:</span></div><div class="card-description"><p>The extraction covers the enrollment and the order; the Customer Price Sheet and the amendments referenced in it have not been reviewed.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Timing and decision window</span><span class="card-title-stat">High urgency</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">The notice date is 62 days away:</span></div><div class="card-description"><p>Written notice is due by {{D+62}} for any change to quantity or vehicle at the {{D+152}} renewal. After it, the agreement renews on the current terms.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">The owner confirmations and the playbook fit before it:</span></div><div class="card-description"><p>The function owners are asked to confirm their inactive seats within three weeks of the request; the playbook follows before round 1. The confirmations are the long pole and should start now.</p></div></div></div></div></div></div></div><div class="report-section" id="who"><div class="report-section-subheader"><p class="report-section-title">Where the idle seats sit, and who owns them</p><p class="report-section-description">Inactive seats are assigned to current staff with no sign-in in 90 days. The departed and never-assigned seats release without an owner.</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 14%"><col style="width: 14%"><col style="width: 12%"><col style="width: 22%"></colgroup><thead><tr><th><span>Function</span></th><th class="report-table-cell-numeric"><span>Active users</span></th><th class="report-table-cell-numeric"><span>Inactive seats</span></th><th class="report-table-cell-numeric"><span>Fit E3</span></th><th><span>Owner</span></th></tr></thead><tbody><tr class="report-table-row-focus"><td><span class="report-table-position">Operations</span></td><td class="report-table-cell-numeric"><span>1,500</span></td><td class="report-table-cell-numeric"><span class="warning">640</span></td><td class="report-table-cell-numeric"><span>600</span></td><td><span>Marcus Oyelaran</span></td></tr><tr><td><span class="report-table-position">Advisory and wealth</span></td><td class="report-table-cell-numeric"><span>2,050</span></td><td class="report-table-cell-numeric"><span>520</span></td><td class="report-table-cell-numeric"><span>300</span></td><td><span>Lena Graf</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Client services</span></td><td class="report-table-cell-numeric"><span>2,360</span></td><td class="report-table-cell-numeric"><span class="warning">510</span></td><td class="report-table-cell-numeric"><span>900</span></td><td><span>Dana Whitfield</span></td></tr><tr><td><span class="report-table-position">Technology</span></td><td class="report-table-cell-numeric"><span>2,300</span></td><td class="report-table-cell-numeric"><span>380</span></td><td class="report-table-cell-numeric"><span>100</span></td><td><span>Jonas Mills</span></td></tr><tr><td><span class="report-table-position">Corporate functions</span></td><td class="report-table-cell-numeric"><span>690</span></td><td class="report-table-cell-numeric"><span>340</span></td><td class="report-table-cell-numeric"><span>200</span></td><td><span>Adrian Meyer</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>8,900</span></td><td class="report-table-cell-numeric"><span>2,390</span></td><td class="report-table-cell-numeric"><span>2,100</span></td><td><span></span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-card callout warning"><div class="card-title-row"><span class="card-title warning">Who confirms</span></div><div class="card-description"><p>Operations and Client services hold half of the inactive seats. Each owner is asked one question: of the inactive seats in their function, how many can be released at the renewal. Nothing is released without that answer.</p></div></div></div></div><div class="report-section" id="assessment"><div class="report-section-subheader-row"><p class="report-section-title">Assessment and recommendation</p><p class="report-section-stat warning">High priority</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 18%"><col></colgroup><thead><tr><th><span>Factor</span></th><th><span>Assessment</span></th><th><span>Rationale</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Financial opportunity</span></td><td><span class="positive">Strong</span></td><td><span>$2.4M to $3.4M a year on an $8.5M agreement; the quantity lever alone is evidenced seat by seat.</span></td></tr><tr><td><span class="report-table-position">Negotiation leverage</span></td><td><span>Moderate</span></td><td><span>Usage data makes the reduction a matter of record and MCA-E is a credible vehicle; leaving Microsoft is not.</span></td></tr><tr><td><span class="report-table-position">Decision urgency</span></td><td><span class="warning">High</span></td><td><span>Notice by {{D+62}}; owner confirmations and a playbook must land before it.</span></td></tr><tr><td><span class="report-table-position">Contractual risk</span></td><td><span class="negative">High</span></td><td><span>No right to reduce and no price protection: renewing as is locks quantity and price for three years.</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">If you request the playbook</span></div><div class="card-description"><p>The five function owners are asked to confirm their inactive seats within three weeks, as tasks on this project. The <strong>negotiation playbook</strong> is delivered before round 1, built on the confirmed quantity, and a <strong>savings report</strong> follows signing. Letting it renew records the decision; a reminder brings this report back on the date you choose.</p></div></div></div></div><div class="report-section" id="sources"><div class="report-section-subheader-row"><p class="report-section-title">Sources</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 35%"><col style="width: 20%"><col></colgroup><thead><tr><th><span>Source</span></th><th><span>As of</span></th><th><span>Used for</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Microsoft 365 admin center usage export</span></td><td><span>{{D-5}}</span></td><td><span>Licenses assigned and last activity per user</span></td></tr><tr><td><span class="report-table-position">Entra ID sign-in logs</span></td><td><span>{{D-1}}</span></td><td><span>Daily activity to confirm the export</span></td></tr><tr><td><span class="report-table-position">Workday</span></td><td><span>{{D-10}}</span></td><td><span>Departed staff still holding a license</span></td></tr><tr><td><span class="report-table-position">Coupa AP</span></td><td><span>{{D-6}}</span></td><td><span>Unit price and annual cost</span></td></tr><tr><td><span class="report-table-position">Enterprise Agreement extraction</span></td><td><span>{{D-48}}</span></td><td><span>Term, notice date, reduction and price terms</span></td></tr><tr><td><span class="report-table-position">Market data</span></td><td><span>{{D-1}}</span></td><td><span>Peer median and target price</span></td></tr></tbody></table></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;

export const OPPORTUNITY_SNOWFLAKE = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-warning wide"><div class="label label-warning">Recommendation</div><div class="report-tag report-tag-warning">Rebalance now and pull the renewal forward</div><div class="card-title-container"><div class="card-title"><span class="truncate">Snowflake capacity commitment</span></div><div class="card-subtitle"><span class="card-subtitle">Opportunity report · Consumption · Term {{D-198}} to {{D+166}}, notice by {{D+107}}</span></div></div><div class="card-description"><p>The account consumes its <strong>$6.0M</strong> commitment at <strong>118% pace</strong>: $7.1M a year over the last 90 days, the commitment reached in {{MON+121}} and <strong>$1.1M of overage</strong> billed at the on-demand rate before the term ends. Development and test warehouses explain a third of the growth. Rebalancing them now and signing the next term early turns the overage into leverage on the rate.</p></div></div><div class="report-summary-card report-summary-card-positive narrow"><div class="label label-positive">Opportunity at a glance</div><div class="report-tag report-tag-positive">High opportunity</div><div class="card-title-container"><div class="card-title"><span class="truncate">$1.1M</span></div><div class="card-subtitle"><span class="card-subtitle">Overage avoided this term</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Three-year effect</span><span class="card-data-row-value">$2.4M lower than renewing as is</span></div><div class="card-data-row"><span class="card-data-row-label">Annual commitment</span><span class="card-data-row-value">$6.0M</span></div><div class="card-data-row"><span class="card-data-row-label">Consumed to date</span><span class="card-data-row-value">$3.65M at 54% of the term</span></div></div></div><div class="report-summary-card report-summary-card-warning narrow"><div class="label label-warning">Priority</div><div class="report-tag report-tag-warning">High priority</div><div class="card-title-container"><div class="card-title"><span class="truncate">Act before {{MON+121}}</span></div><div class="card-subtitle"><span class="card-subtitle">The commitment runs out mid-term; every month after it bills 20% above the capacity rate</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Financial opportunity</span><span class="card-data-row-value">Strong</span></div><div class="card-data-row"><span class="card-data-row-label">Negotiation leverage</span><span class="card-data-row-value">Strong</span></div><div class="card-data-row"><span class="card-data-row-label">Decision urgency</span><span class="card-data-row-value">High</span></div><div class="card-data-row"><span class="card-data-row-label">Contractual risk</span><span class="card-data-row-value">Moderate</span></div></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="report">Opportunity report</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="report"><button class="report-sidenav-btn" data-target="about"><span>About the deal</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="glance"><span>Consumption against the commitment</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="terms"><span>Contract terms that matter</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="options"><span>What to do about it</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="assessment"><span>Assessment and recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="report"><div class="report-section" id="about"><div class="report-section-subheader"><p class="report-section-title">About the deal</p><p class="report-section-description">Prepared {{D-1}} from the billing feed; an estimate before the data platform owner confirms, not a playbook.</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 28%"><col></colgroup><thead><tr><th><span>Item</span></th><th><span>Detail</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Supplier</span></td><td><span>Snowflake</span></td></tr><tr><td><span class="report-table-position">Agreement</span></td><td><span>Capacity commitment, $6.0M a year, $2.40 a credit</span></td></tr><tr><td><span class="report-table-position">Deal type</span></td><td><span>Consumption commitment, year 1</span></td></tr><tr><td><span class="report-table-position">Term</span></td><td><span>{{D-198}} to {{D+166}}</span></td></tr><tr><td><span class="report-table-position">Notice date</span></td><td><span>{{D+107}}; 60 days before the term ends</span></td></tr><tr><td><span class="report-table-position">Owner</span></td><td><span>Data platform; negotiation owner Rachel Sato</span></td></tr><tr><td><span class="report-table-position">Source</span></td><td><span>Snowflake ACCOUNT_USAGE metering, daily billing feed to {{D-1}}</span></td></tr></tbody></table></div></div><div class="report-section" id="glance"><div class="report-section-subheader-row"><p class="report-section-title">Consumption against the commitment</p><p class="report-section-stat negative">118% pace · $1.1M overage ahead</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Commitment</span><span class="report-stat-grid-value">$6.0M</span><span class="report-stat-grid-sub">365 days, 198 elapsed (54%)</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Consumed to date</span><span class="report-stat-grid-value warning">$3.65M</span><span class="report-stat-grid-sub">61% of the commitment at 54% of the term</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Pace, last 90 days</span><span class="report-stat-grid-value negative">118%</span><span class="report-stat-grid-sub">$7.1M a year; commitment reached in {{MON+121}}</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Projected overage</span><span class="report-stat-grid-value negative">$1.1M</span><span class="report-stat-grid-sub">$0.9M above the commitment at the on-demand rate</span></div></div></div><div class="report-section-card-container"><div class="report-vega-chart"><p class="report-table-title">Consumption against the $6.0M commitment: actual to {{DS-1}}, the forecast at the last 90 days&apos; pace, the commitment reached in {{MON+121}} and $0.9M over it by {{DS+166}}</p><div class="report-vega-chart-svg"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" class="marks" width="1119" height="336" viewBox="0 0 1119 336"><g fill="none" stroke-miterlimit="4" transform="translate(55,38)"><g class="mark-group role-frame root" role="graphics-object" aria-roledescription="group mark container"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0.5,0.5h860v260h-860Z"/><g><g class="mark-group role-axis" aria-hidden="true"><g transform="translate(0.5,260.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-grid" pointer-events="none"><line transform="translate(0,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(72,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(143,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(215,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(287,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(358,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(430,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(502,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(573,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(645,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(717,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(788,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(860,-260)" x2="0" y2="260" stroke="#F0F0F0" stroke-width="1" opacity="1"/></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-axis" aria-hidden="true"><g transform="translate(0.5,0.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-grid" pointer-events="none"><line transform="translate(0,260)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,223)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,186)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,149)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,111)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,74)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,37)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/><line transform="translate(0,0)" x2="860" y2="0" stroke="#F0F0F0" stroke-width="1" opacity="1"/></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-axis" role="graphics-symbol" aria-roledescription="axis" aria-label="X-axis titled 'Months into the term (12 is the end of the term)' for a linear scale with values from 0 to 12"><g transform="translate(0.5,260.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-tick" pointer-events="none"><line transform="translate(0,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(72,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(143,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(215,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(287,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(358,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(430,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(502,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(573,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(645,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(717,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(788,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(860,0)" x2="0" y2="5" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-label" pointer-events="none"><text text-anchor="start" transform="translate(0,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">0</text><text text-anchor="middle" transform="translate(71.66666666666666,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">1</text><text text-anchor="middle" transform="translate(143.33333333333331,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">2</text><text text-anchor="middle" transform="translate(215,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">3</text><text text-anchor="middle" transform="translate(286.66666666666663,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">4</text><text text-anchor="middle" transform="translate(358.33333333333337,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">5</text><text text-anchor="middle" transform="translate(430,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">6</text><text text-anchor="middle" transform="translate(501.6666666666667,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">7</text><text text-anchor="middle" transform="translate(573.3333333333333,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">8</text><text text-anchor="middle" transform="translate(645,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">9</text><text text-anchor="middle" transform="translate(716.6666666666667,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">10</text><text text-anchor="middle" transform="translate(788.3333333333333,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">11</text><text text-anchor="end" transform="translate(860,16)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">12</text></g><g class="mark-rule role-axis-domain" pointer-events="none"><line transform="translate(0,0)" x2="860" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-title" pointer-events="none"><text text-anchor="middle" transform="translate(430,31)" font-family="Inter, sans-serif" font-size="12px" font-weight="600" fill="#404040" opacity="1">Months into the term (12 is the end of the term)</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-axis" role="graphics-symbol" aria-roledescription="axis" aria-label="Y-axis titled 'Cumulative consumption' for a linear scale with values from $0 to $7M"><g transform="translate(0.5,0.5)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-rule role-axis-tick" pointer-events="none"><line transform="translate(0,260)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,223)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,186)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,149)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,111)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,74)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,37)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/><line transform="translate(0,0)" x2="-5" y2="0" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-label" pointer-events="none"><text text-anchor="end" transform="translate(-7,263)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$0</text><text text-anchor="end" transform="translate(-7,225.85714285714286)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$1M</text><text text-anchor="end" transform="translate(-7,188.71428571428572)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$2M</text><text text-anchor="end" transform="translate(-7,151.57142857142856)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$3M</text><text text-anchor="end" transform="translate(-7,114.42857142857143)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$4M</text><text text-anchor="end" transform="translate(-7,77.28571428571428)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$5M</text><text text-anchor="end" transform="translate(-7,40.14285714285715)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$6M</text><text text-anchor="end" transform="translate(-7,3)" font-family="Inter, sans-serif" font-size="11px" fill="#666666" opacity="1">$7M</text></g><g class="mark-rule role-axis-domain" pointer-events="none"><line transform="translate(0,260)" x2="0" y2="-260" stroke="#D9D9D9" stroke-width="1" opacity="1"/></g><g class="mark-text role-axis-title" pointer-events="none"><text text-anchor="middle" transform="translate(-37,130) rotate(-90) translate(0,-3)" font-family="Inter, sans-serif" font-size="12px" font-weight="600" fill="#404040" opacity="1">Cumulative consumption</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g><g class="mark-group role-scope layer_0_layer_0_pathgroup" role="graphics-object" aria-roledescription="group mark container"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h860v260h-860Z"/><g><g class="mark-line role-mark layer_0_layer_0_marks" role="graphics-object" aria-roledescription="line mark container"><path aria-label="t: 0; value: $0; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="line mark" d="M0,260L37.983,250.714L110.367,233.257L181.317,214.686L254.417,194.629L327.517,172.343L397.75,149.314L463.683,124.429" stroke="#006ACC" stroke-width="2" stroke-dasharray="1,0"/></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h860v260h-860Z"/><g><g class="mark-line role-mark layer_0_layer_0_marks" role="graphics-object" aria-roledescription="line mark container"><path aria-label="t: 6; value: $4M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="line mark" d="M463.683,124.429L541.8,101.4L614.183,79.486L687.283,57.571L751.067,37.143L753.217,36.4L857.133,3.714" stroke="#2FB801" stroke-width="2" stroke-dasharray="6,4"/></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h860v260h-860Z"/><g><g class="mark-line role-mark layer_0_layer_0_marks" role="graphics-object" aria-roledescription="line mark container"><path aria-label="t: 0; value: $6M; series: Commitment; kind: commitment" role="graphics-symbol" aria-roledescription="line mark" d="M0,37.143L857.133,37.143" stroke="#FF8400" stroke-width="2" stroke-dasharray="2,2"/></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g></g><g class="mark-symbol role-mark layer_0_layer_1_marks" role="graphics-object" aria-roledescription="symbol mark container"><path aria-label="t: 0; value: $0; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(0,260)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 1; value: $300k; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(37.983333333333334,250.71428571428572)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 2; value: $700k; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(110.36666666666666,233.25714285714287)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 3; value: $1M; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(181.31666666666666,214.68571428571428)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 4; value: $2M; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(254.41666666666666,194.62857142857143)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 5; value: $2M; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(327.5166666666667,172.34285714285713)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 6; value: $3M; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(397.74999999999994,149.31428571428572)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 6; value: $4M; series: Consumed; kind: actual" role="graphics-symbol" aria-roledescription="point" transform="translate(463.68333333333334,124.42857142857142)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#006ACC" stroke-width="2" stroke-dasharray="1,0" opacity="1"/><path aria-label="t: 6; value: $4M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="point" transform="translate(463.68333333333334,124.42857142857142)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="t: 8; value: $4M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="point" transform="translate(541.8,101.4)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="t: 9; value: $5M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="point" transform="translate(614.1833333333334,79.48571428571428)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="t: 10; value: $5M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="point" transform="translate(687.2833333333333,57.57142857142857)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="t: 10; value: $6M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="point" transform="translate(751.0666666666667,37.14285714285715)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="t: 11; value: $6M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="point" transform="translate(753.2166666666667,36.400000000000006)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="t: 12; value: $7M; series: Forecast at the last 90 days' pace; kind: forecast" role="graphics-symbol" aria-roledescription="point" transform="translate(857.1333333333333,3.714285714285701)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#2FB801" stroke-width="2" stroke-dasharray="6,4" opacity="1"/><path aria-label="t: 0; value: $6M; series: Commitment; kind: commitment" role="graphics-symbol" aria-roledescription="point" transform="translate(0,37.14285714285715)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#FF8400" stroke-width="2" stroke-dasharray="2,2" opacity="1"/><path aria-label="t: 12; value: $6M; series: Commitment; kind: commitment" role="graphics-symbol" aria-roledescription="point" transform="translate(857.1333333333333,37.14285714285715)" d="M2.449,0A2.449,2.449,0,1,1,-2.449,0A2.449,2.449,0,1,1,2.449,0" fill="#FF8400" stroke-width="2" stroke-dasharray="2,2" opacity="1"/></g><g class="mark-text role-mark layer_1_marks" role="graphics-object" aria-roledescription="text mark container"><text aria-label="t: 10; value: 6000000; label: Commitment reached, month 11 of 12" role="graphics-symbol" aria-roledescription="text mark" text-anchor="start" transform="translate(759.0666666666667,32.14285714285715)" font-family="Inter, sans-serif" font-size="11px" fill="black">Commitment reached, month 11 of 12</text></g><g class="mark-symbol role-mark layer_2_marks" role="graphics-object" aria-roledescription="symbol mark container"><path aria-label="t: 10; value: 6000000" role="graphics-symbol" aria-roledescription="point" transform="translate(751.0666666666667,37.14285714285715)" d="M4.743,0A4.743,4.743,0,1,1,-4.743,0A4.743,4.743,0,1,1,4.743,0" fill="#fd3559" stroke-width="2" opacity="0.7"/></g><g class="mark-group role-legend" role="graphics-symbol" aria-roledescription="legend" aria-label="Symbol legend for fill color and stroke color with 3 values: Consumed, Forecast at the last 90 days' pace, Commitment"><g transform="translate(0,-33)"><path class="background" aria-hidden="true" d="M0,0h388v14h-388Z" pointer-events="none"/><g><g class="mark-group role-legend-entry"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h0v0h0Z" pointer-events="none"/><g><g class="mark-group role-scope" role="graphics-object" aria-roledescription="group mark container"><g transform="translate(0,0)"><path class="background" aria-hidden="true" d="M0,0h86v14h-86Z" pointer-events="none" opacity="1"/><g><g class="mark-symbol role-legend-symbol" pointer-events="none"><path transform="translate(6,6)" d="M-5,-5h10v10h-10Z" fill="#006ACC" stroke="#006ACC" stroke-width="1.5" opacity="1"/></g><g class="mark-text role-legend-label" pointer-events="none"><text text-anchor="start" transform="translate(16,9)" font-family="Inter, sans-serif" font-size="11px" fill="#404040" opacity="1">Consumed</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g><g transform="translate(98,0)"><path class="background" aria-hidden="true" d="M0,0h174v14h-174Z" pointer-events="none" opacity="1"/><g><g class="mark-symbol role-legend-symbol" pointer-events="none"><path transform="translate(6,6)" d="M-5,-5h10v10h-10Z" fill="#2FB801" stroke="#2FB801" stroke-width="1.5" opacity="1"/></g><g class="mark-text role-legend-label" pointer-events="none"><text text-anchor="start" transform="translate(16,9)" font-family="Inter, sans-serif" font-size="11px" fill="#404040" opacity="1">Forecast at the l…</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g><g transform="translate(284,0)"><path class="background" aria-hidden="true" d="M0,0h104v14h-104Z" pointer-events="none" opacity="1"/><g><g class="mark-symbol role-legend-symbol" pointer-events="none"><path transform="translate(6,6)" d="M-5,-5h10v10h-10Z" fill="#FF8400" stroke="#FF8400" stroke-width="1.5" opacity="1"/></g><g class="mark-text role-legend-label" pointer-events="none"><text text-anchor="start" transform="translate(16,9)" font-family="Inter, sans-serif" font-size="11px" fill="#404040" opacity="1">Commitment</text></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g></g><path class="foreground" aria-hidden="true" d="" pointer-events="none" display="none"/></g></g></g><path class="foreground" aria-hidden="true" d="" display="none"/></g></g></g></svg></div></div></div><div class="report-section-card-container"><div class="report-waterfall-wrap"><svg class="report-waterfall" width="980" height="320" viewBox="0 0 980 320" xmlns="http://www.w3.org/2000/svg"><line x1="80" x2="955" y1="265" y2="265" class="report-waterfall-baseline"></line><text x="70" y="269" text-anchor="end" class="report-waterfall-tick">$0M</text><line x1="80" x2="955" y1="210" y2="210" class="report-waterfall-gridline"></line><text x="70" y="214" text-anchor="end" class="report-waterfall-tick">$0.4M</text><line x1="80" x2="955" y1="155" y2="155" class="report-waterfall-gridline"></line><text x="70" y="159" text-anchor="end" class="report-waterfall-tick">$0.8M</text><line x1="80" x2="955" y1="100" y2="100" class="report-waterfall-gridline"></line><text x="70" y="104" text-anchor="end" class="report-waterfall-tick">$1.1M</text><line x1="80" x2="955" y1="45" y2="45" class="report-waterfall-gridline"></line><text x="70" y="49" text-anchor="end" class="report-waterfall-tick">$1.5M</text><rect x="144.375" y="103.66666666666669" width="90" height="161.33333333333331" rx="4" class="report-waterfall-bar report-waterfall-bar-total"></rect><text x="189.375" y="94.66666666666669" text-anchor="middle" class="report-waterfall-value report-waterfall-value-total">$1,100,000</text><text x="189.375" y="283" text-anchor="middle" class="report-waterfall-label">Overage ahead this term</text><line x1="234.375" x2="363.125" y1="103.66666666666669" y2="103.66666666666669" class="report-waterfall-connector"></line><rect x="363.125" y="103.66666666666669" width="90" height="88" rx="4" class="report-waterfall-bar report-waterfall-bar-captured-saving"></rect><text x="408.125" y="94.66666666666669" text-anchor="middle" class="report-waterfall-value report-waterfall-value-captured-saving">−$600,000</text><text x="408.125" y="283" text-anchor="middle" class="report-waterfall-label">Suspend 11 development and test warehouses</text><line x1="453.125" x2="581.875" y1="191.66666666666669" y2="191.66666666666669" class="report-waterfall-connector"></line><rect x="581.875" y="191.66666666666669" width="90" height="73.33333333333331" rx="4" class="report-waterfall-bar report-waterfall-bar-avoided-exposure"></rect><text x="626.875" y="182.66666666666669" text-anchor="middle" class="report-waterfall-value report-waterfall-value-avoided-exposure">−$500,000</text><text x="626.875" y="283" text-anchor="middle" class="report-waterfall-label">Early renewal prices the rest at the capacity rate</text><line x1="671.875" x2="800.625" y1="265" y2="265" class="report-waterfall-connector"></line><rect x="800.625" y="265" width="90" height="2" rx="4" class="report-waterfall-bar report-waterfall-bar-total"></rect><text x="845.625" y="256" text-anchor="middle" class="report-waterfall-value report-waterfall-value-total">$0</text><text x="845.625" y="283" text-anchor="middle" class="report-waterfall-label">Overage after acting</text></svg><div class="report-waterfall-legend"><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-total"></span>Total</span><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-captured-saving"></span>Captured Saving</span><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-avoided-exposure"></span>Avoided Exposure</span></div></div></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">Where the consumption goes, last 90 days annualized</p><table class="report-table"><colgroup><col><col style="width: 12%"><col style="width: 16%"><col style="width: 14%"><col style="width: 18%"></colgroup><thead><tr><th><span>Workload</span></th><th class="report-table-cell-numeric"><span>Share</span></th><th class="report-table-cell-numeric"><span>Annualized</span></th><th class="report-table-cell-numeric"><span>Since {{MON-121}}</span></th><th><span>Owner</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Data engineering pipelines</span></td><td class="report-table-cell-numeric"><span>36%</span></td><td class="report-table-cell-numeric"><span>$2.56M</span></td><td class="report-table-cell-numeric"><span>+8%</span></td><td><span>Jonas Mills</span></td></tr><tr><td><span class="report-table-position">Analytics and reporting</span></td><td class="report-table-cell-numeric"><span>25%</span></td><td class="report-table-cell-numeric"><span>$1.78M</span></td><td class="report-table-cell-numeric"><span>+5%</span></td><td><span>Priya Kapoor</span></td></tr><tr><td><span class="report-table-position">Data science</span></td><td class="report-table-cell-numeric"><span>18%</span></td><td class="report-table-cell-numeric"><span>$1.28M</span></td><td class="report-table-cell-numeric"><span>+14%</span></td><td><span>Tom Hale</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Development and test</span></td><td class="report-table-cell-numeric"><span>16%</span></td><td class="report-table-cell-numeric"><span>$1.14M</span></td><td class="report-table-cell-numeric"><span class="negative">+60%</span></td><td><span>Tom Hale</span></td></tr><tr><td><span class="report-table-position">Other</span></td><td class="report-table-cell-numeric"><span>5%</span></td><td class="report-table-cell-numeric"><span>$0.34M</span></td><td class="report-table-cell-numeric"><span>+2%</span></td><td><span>Data platform</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>100%</span></td><td class="report-table-cell-numeric"><span>$7.1M</span></td><td class="report-table-cell-numeric"><span>+13%</span></td><td><span></span></td></tr></tbody></table></div></div><div class="report-section-card-container"><div class="report-card callout warning"><div class="card-title-row"><span class="card-title warning">What drives the pace</span></div><div class="card-description"><p>Development and test warehouses run <strong>without auto-suspend on 11 of 14 environments</strong>. That alone explains $0.35M of the annual pace, and it is the part of the growth nobody chose. Tom Hale owns the development and test environments; he is asked which can be suspended when idle.</p></div></div></div></div><div class="report-section" id="terms"><div class="report-section-subheader-row"><p class="report-section-title">Contract terms that matter</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 32%"><col style="width: 34%"><col></colgroup><thead><tr><th><span>Term</span></th><th><span>Today</span></th><th><span>Why it matters</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Capacity rate</span></td><td><span>$2.40 a credit</span></td><td><span>The rate the commitment is bought at; the next term&apos;s rate is the negotiation.</span></td></tr><tr><td><span class="report-table-position">On-demand rate above the commitment</span></td><td><span>$2.88 a credit, 20% higher</span></td><td><span>Every credit past the commitment costs 20% more; the overage is billed at this rate.</span></td></tr><tr><td><span class="report-table-position">Rollover of unused capacity</span></td><td><span>None</span></td><td><span>A commitment sized too high is lost; a top-up bought now and not used is lost too.</span></td></tr><tr><td><span class="report-table-position">Early renewal</span></td><td><span>Allowed; unused balance carries into the new term</span></td><td><span>The next term can start before this one ends, so the overage months can be priced at the capacity rate instead.</span></td></tr></tbody></table></div></div><div class="report-section" id="options"><div class="report-section-subheader"><p class="report-section-title">What to do about it</p><p class="report-section-description">Three options with their effect on this term and on the renewal. The recommended one is what the playbook is built for.</p></div><div class="report-section-card-container"><div class="report-card-grid" style="grid-template-columns: repeat(3, minmax(0, 1fr));"><div class="report-card"><span class="label">Recommended</span><div class="card-title-row"><span class="card-title">1. Rebalance now and pull the renewal forward</span><span class="card-title-stat">$2.4M lower over three years</span></div><p class="report-text">Auto-suspend and right-size the development and test warehouses, which brings the pace to $6.4M, then sign the next capacity term in {{MON+73}} at a higher commitment and a lower rate.</p><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">Overage this term</span><span class="report-metric-list-value positive">$0 instead of $1.1M</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Rate on the new term</span><span class="report-metric-list-value positive">$2.21 a credit, 8% lower</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Next-term commitment</span><span class="report-metric-list-value">$7.5M with a 10% flex band</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Three-year effect</span><span class="report-metric-list-value positive">$2.4M lower than renewing as is</span></div></div></div><div class="report-card"><span class="label">Fallback</span><div class="card-title-row"><span class="card-title">2. Rebalance only</span><span class="card-title-stat">$0.6M lower</span></div><p class="report-text">Auto-suspend and right-size development and test; keep the current term and commitment, and renew on {{D+167}} at the market rate then.</p><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">Overage this term</span><span class="report-metric-list-value">$0.5M instead of $1.1M</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Rate</span><span class="report-metric-list-value">Unchanged</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Renewal</span><span class="report-metric-list-value">{{D+167}} at the market rate then</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Three-year effect</span><span class="report-metric-list-value">$0.6M lower</span></div></div></div><div class="report-card"><span class="label">Not recommended</span><div class="card-title-row"><span class="card-title">3. Buy a top-up now</span><span class="card-title-stat">$0.9M lower</span></div><p class="report-text">Add $1.0M of capacity to the current term at the capacity rate to avoid on-demand billing. No leverage is used and the growth is not priced in.</p><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">Overage this term</span><span class="report-metric-list-value">$0.2M instead of $1.1M</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Rate</span><span class="report-metric-list-value">Unchanged, no leverage used</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Renewal</span><span class="report-metric-list-value">Unchanged; the growth is not priced in</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Three-year effect</span><span class="report-metric-list-value">$0.9M lower</span></div></div></div></div></div></div><div class="report-section" id="assessment"><div class="report-section-subheader-row"><p class="report-section-title">Assessment and recommendation</p><p class="report-section-stat warning">High priority</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 18%"><col></colgroup><thead><tr><th><span>Factor</span></th><th><span>Assessment</span></th><th><span>Rationale</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Financial opportunity</span></td><td><span class="positive">Strong</span></td><td><span>$1.1M of overage avoided this term and $2.4M over three years against renewing as is.</span></td></tr><tr><td><span class="report-table-position">Negotiation leverage</span></td><td><span class="positive">Strong</span></td><td><span>A larger commitment, signed early, is what Snowflake&apos;s field wants before its year end; the rate is the price of it.</span></td></tr><tr><td><span class="report-table-position">Decision urgency</span></td><td><span class="warning">High</span></td><td><span>The commitment is reached in {{MON+121}}; the early renewal must be signed in {{MON+73}} to price those months at the capacity rate.</span></td></tr><tr><td><span class="report-table-position">Contractual risk</span></td><td><span>Moderate</span></td><td><span>No rollover and a 20% on-demand premium; the flex band on the next term is the protection to secure.</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">If you request the playbook</span></div><div class="card-description"><p>The data platform owner is asked to confirm which development and test environments can be suspended when idle, as a task on this project. The <strong>early renewal playbook</strong> follows before {{MON+73}}, sized from the answer, and a <strong>savings report</strong> after signing. Dismissing records the reason so the report does not come back; a reminder brings it back on the date you choose.</p></div></div></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;

export const PLAYBOOK_MICROSOFT = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-positive wide"><div class="label label-positive">Target outcome</div><div class="report-tag report-tag-positive">Playbook v1</div><div class="card-title-container"><div class="card-title"><span class="truncate">$2.9M a year target</span></div><div class="card-subtitle"><span class="card-subtitle">Right-size and re-price the Microsoft EA at the {{D+152}} renewal</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Quantity and edition, from the usage analysis</span><span class="card-data-row-value">$1.9M a year</span></div><div class="card-data-row"><span class="card-data-row-label">Price, at the peer target</span><span class="card-data-row-value">$1.0M a year</span></div><div class="card-data-row"><span class="card-data-row-label">Snapshot&apos;s rough estimate</span><span class="card-data-row-value">$2.4M to $3.4M</span></div></div></div><div class="report-summary-card report-summary-card-warning wide"><div class="label label-warning">Round 1</div><div class="card-title-container"><div class="card-title"><span class="truncate">In progress</span></div><div class="card-subtitle"><span class="card-subtitle">Opening position ready to send; vendor response expected within two weeks</span></div></div><div class="card-description"><p>Notice by <strong>{{D+62}}</strong>. The five owners&apos; confirmations of 3 and {{DS+6}} settle the quantity at 10,400; round 1 opens on it.</p></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="execute">How to execute</button><button class="report-utility-nav-btn" data-tab-id="usage">Usage analysis</button><button class="report-utility-nav-btn" data-tab-id="about">About the deal</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="execute"><button class="report-sidenav-btn" data-target="moves"><span>Negotiation moves</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="scenarios"><span>Estimated savings against today</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="asks"><span>Recommended commercial ask package</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="protections"><span>Protections to secure before signature</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div><div class="report-tab-sidenav" data-tab-id="usage" hidden=""><button class="report-sidenav-btn" data-target="recommendation"><span>Recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="quantity"><span>How the quantity was set</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="edition-mix"><span>Edition mix</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="range"><span>What is kept</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="asks"><span>What to ask for</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="vendor-response"><span>Vendor response and the net saving</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="after-signing"><span>After signing</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="evidence"><span>Evidence</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div><div class="report-tab-sidenav" data-tab-id="about" hidden=""><button class="report-sidenav-btn" data-target="deal"><span>The deal today</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="benchmark"><span>Market benchmark</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="risks"><span>Risks</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="execute"><div class="report-section" id="moves"><div class="report-section-subheader"><p class="report-section-title">Negotiation moves</p><p class="report-section-description">In the order to play them: the quantity is settled on evidence before any price is discussed.</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 38%"><col></colgroup><thead><tr><th><span>Move</span></th><th><span>Why it works</span></th><th><span>What to do</span></th></tr></thead><tbody><tr><td><span class="report-table-position">1. Settle the quantity first</span></td><td><span>The usage analysis makes 10,400 licenses a matter of record: 550 never assigned, 560 held by departed staff, 2,180 confirmed not needed by their owners, 1,290 planned hires. Microsoft&apos;s own usage reports show the same.</span></td><td><span>Table the reconciliation from bought to needed as the order, before any discussion of price. Do not let the count be traded for a discount.</span></td></tr><tr><td><span class="report-table-position">2. Release the empty seats unconditionally</span></td><td><span>1,110 seats are not in anyone&apos;s hands; nothing about them is negotiable and conceding them costs Microsoft nothing to accept.</span></td><td><span>Take the never-assigned and departed seats off the order in round 1, then table the owners&apos; confirmations for the 2,180 inactive ones.</span></td></tr><tr><td><span class="report-table-position">3. Hold the E5 count at 8,300</span></td><td><span>The owners&apos; confirmations settle the count: 6,800 active E5 users, 210 kept seats and 1,290 hires. Each 100 seats conceded back is $58K a year at the target price.</span></td><td><span>Quote 8,300 E5 as a settled number with the confirmations attached. Do not round up to close early, and do not trade the count for a discount.</span></td></tr><tr><td><span class="report-table-position">4. Price the smaller order at the peer target</span></td><td><span>23 comparable agreements put the median at $49.50 and the lower quartile at $46.80; $57.00 sits above the upper quartile.</span></td><td><span>Ask $46.80 as the opening position with the benchmark on the table; settle at $48.00; walk at $49.50 only with the reduction right.</span></td></tr><tr><td><span class="report-table-position">5. Keep the E3 mix as currency</span></td><td><span>2,100 users use no E5-only feature; moving them is $0.5M a year on its own, and Microsoft would rather keep them on E5 at a lower price.</span></td><td><span>Hold the E3 move in reserve: trade it back for a better E5 price only if the discount tier or the price moves below target.</span></td></tr><tr><td><span class="report-table-position">6. Make MCA-E the credible alternative</span></td><td><span>Leaving Microsoft 365 is not credible in the window; moving the licensing vehicle is, and an EA renewal is worth more to the field than a shrinking MCA-E.</span></td><td><span>Request the MCA-E price comparison (in progress) and reference it in round 2 without naming it as the plan.</span></td></tr><tr><td><span class="report-table-position">7. Keep Copilot out of the EA</span></td><td><span>460 of 1,000 Copilot seats are inactive; bundling it protects Microsoft&apos;s total, not ours.</span></td><td><span>Decline any bundle. If Copilot stays, re-size it to 600 in a separate order with its own usage clause.</span></td></tr><tr><td><span class="report-table-position">8. Hold the term until late</span></td><td><span>A three-year commitment is the strongest concession left and should buy price protection, not be assumed.</span></td><td><span>Negotiate the first-year economics on a one-year basis, then ask what the three-year term earns: the price lock and the reduction right.</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">Rounds</p><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 26%"><col></colgroup><thead><tr><th><span>Round</span></th><th><span>When</span></th><th><span>Goal</span></th></tr></thead><tbody><tr class="report-table-row-focus"><td><span class="report-table-position">Round 1: opening position</span></td><td><span>Sent {{D+7}}; response expected {{DS+13}}</span></td><td><span>The quantity of 10,400 on the table with the evidence; $46.80 and the reduction right asked. No Copilot.</span></td></tr><tr><td><span class="report-table-position">Round 2: counter</span></td><td><span>On Microsoft&apos;s response, expected by {{DS+21}}</span></td><td><span>Hold 8,300 E5 and 2,100 E3; trade the E3 mix against price only if the discount tier is raised.</span></td></tr><tr><td><span class="report-table-position">Round 3: close</span></td><td><span>Before the notice date, {{D+62}}</span></td><td><span>Sign at or below $48.00 with the reduction right and the price lock; send notice on the current agreement if Microsoft holds at $57.00.</span></td></tr></tbody></table></div></div></div><div class="report-section" id="scenarios"><div class="report-section-subheader-row"><p class="report-section-title">Estimated savings against today</p><p class="report-section-stat positive">$2.9M target</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">10,400 licenses (8,300 E5 and 2,100 E3) against 12,400 E5 at $57.00, $8.5M a year</p><table class="report-table"><colgroup><col><col style="width: 20%"><col style="width: 16%"><col style="width: 22%"></colgroup><thead><tr><th><span>Position</span></th><th class="report-table-cell-numeric"><span>E5 / E3 a user a month</span></th><th class="report-table-cell-numeric"><span>Annual cost</span></th><th class="report-table-cell-numeric"><span>Annual saving</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Opening ask</span></td><td class="report-table-cell-numeric"><span>$46.80 / $30.00</span></td><td class="report-table-cell-numeric"><span>$5.4M</span></td><td class="report-table-cell-numeric"><span>$3.1M (36%)</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Target</span></td><td class="report-table-cell-numeric"><span>$48.00 / $32.00</span></td><td class="report-table-cell-numeric"><span>$5.6M</span></td><td class="report-table-cell-numeric"><span class="positive">$2.9M (34%)</span></td></tr><tr><td><span class="report-table-position">Fallback</span></td><td class="report-table-cell-numeric"><span>$49.50 / $34.00</span></td><td class="report-table-cell-numeric"><span>$5.8M</span></td><td class="report-table-cell-numeric"><span>$2.7M (32%)</span></td></tr><tr><td><span class="report-table-position">Quantity only, today&apos;s prices</span></td><td class="report-table-cell-numeric"><span>$57.00 / $36.00</span></td><td class="report-table-cell-numeric"><span>$6.6M</span></td><td class="report-table-cell-numeric"><span>$1.9M (22%)</span></td></tr></tbody></table></div></div><div class="report-section-card-container"><div class="report-description-list"><span class="label">Supporting arguments</span><div class="report-description-list-content"><div class="card-title"><span class="truncate">The quantity is evidenced seat by seat:</span></div><div class="card-description"><p>Tenant activity to {{D+5}} and Workday headcount reconcile 12,400 bought to 10,400 needed, with 94% of users matched to the contract. The reduction is a fact to record, not a position to defend.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">The price is above the peer market:</span></div><div class="card-description"><p>Over 23 comparable agreements of 8,000 to 15,000 E5 seats signed in the last 18 months, the median is $49.50 and the lower quartile $46.80. At $57.00 the agreement sits above the upper quartile of $53.20.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Microsoft has the same data:</span></div><div class="card-description"><p>The Microsoft 365 admin center usage reports the account team can see show the inactive seats; there is no version of the numbers to dispute.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">The timing favors us:</span></div><div class="card-description"><p>The notice date and the term&apos;s end both fall inside Microsoft&apos;s fiscal year, when field teams carry quota pressure on renewals and a signed EA counts more than a shrinking MCA-E.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">A lower unit price is not the goal on its own:</span></div><div class="card-description"><p>The saving comes from the smaller order first; a discount offered against keeping 12,400 licenses is worth less than the target and should be declined.</p></div></div></div></div></div><div class="report-section" id="asks"><div class="report-section-subheader-row"><p class="report-section-title">Recommended commercial ask package</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 20%"><col style="width: 32%"><col style="width: 32%"><col></colgroup><thead><tr><th><span>Commercial ask</span></th><th><span>Recommended position</span></th><th><span>Why it matters</span></th><th><span>Likelihood</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Quantity at renewal</span></td><td><span>10,400 licenses: 8,300 E5 and 2,100 E3, fixed from the usage analysis.</span></td><td><span>The whole of the $1.9M usage lever.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">Unit price</span></td><td><span>E5 $48.00 and E3 $32.00; opening $46.80 / $30.00; fallback $49.50 / $34.00.</span></td><td><span>The $1.0M price lever; the fallback keeps $0.8M of it.</span></td><td><span>Medium to high</span></td></tr><tr><td><span class="report-table-position">Right to reduce</span></td><td><span>10% a year during the term without penalty or repricing of the remainder.</span></td><td><span>The agreement allows true-ups only today; the seats confirmed inactive after signing would otherwise stay paid for three years.</span></td><td><span>Medium</span></td></tr><tr><td><span class="report-table-position">Price lock</span></td><td><span>The negotiated price fixed for the term, through the {{D+153}} list rise, with 0% escalation.</span></td><td><span>Microsoft has announced a 5% list increase the day after the term ends.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">Renewal cap</span></td><td><span>Increase at the next renewal capped at 3%; 5% as the fallback.</span></td><td><span>Prevents the saving being recovered at the next renewal, three years on.</span></td><td><span>Medium to high</span></td></tr><tr><td><span class="report-table-position">Add-ons priced with the EA</span></td><td><span>Premier support ($0.6M a year) priced in the same negotiation, separately removable.</span></td><td><span>It renews on the same date and would otherwise be priced after the leverage is spent.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">Copilot kept separate</span></td><td><span>Out of the EA; if retained, 600 seats in a separate order with a usage clause.</span></td><td><span>460 of 1,000 seats are inactive; a bundle protects Microsoft&apos;s total.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">SKU and entitlement protection</span></td><td><span>Negotiated economics and material functionality preserved across successor SKUs.</span></td><td><span>Protects against repackaging during the term.</span></td><td><span>Medium</span></td></tr></tbody></table></div></div><div class="report-section" id="protections"><div class="report-section-subheader-row"><p class="report-section-title">Protections to secure before signature</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 22%"><col style="width: 36%"><col></colgroup><thead><tr><th><span>Protection</span></th><th><span>Current risk</span></th><th><span>Recommended structure</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Term and in-term pricing</span></td><td><span>No price protection: nothing caps the price at the anniversary and the 5% list rise applies from {{D+153}}.</span></td><td><span>The price fixed for the three-year term with 0% escalation, stated in the Customer Price Sheet.</span></td></tr><tr><td><span class="report-table-position">Quantity flexibility</span></td><td><span>True-ups only: a seat kept at the renewal is paid for three years whatever its use.</span></td><td><span>A 10% annual true-down at each anniversary, without repricing the remaining licenses.</span></td></tr><tr><td><span class="report-table-position">Renewal protection</span></td><td><span>The saving could be recovered through the next renewal&apos;s price.</span></td><td><span>A renewal increase cap of 3%, 5% as the fallback; no automatic renewal of the quantity.</span></td></tr><tr><td><span class="report-table-position">Edition mix</span></td><td><span>The 2,100 E3 users could be moved back to E5 by a future bundle or feature change.</span></td><td><span>The E3 entitlement and its price stated per user, with the right to move users between editions at the anniversary.</span></td></tr><tr><td><span class="report-table-position">Add-ons</span></td><td><span>Premier support renews on the same date under a separate agreement, after the EA is signed.</span></td><td><span>Priced in the EA negotiation; separately removable without changing the software economics.</span></td></tr><tr><td><span class="report-table-position">Contract precedence</span></td><td><span>The Customer Price Sheet and the amendments referenced in the enrollment have not been reviewed.</span></td><td><span>The executed enrollment and price sheet control over online terms; every referenced amendment listed and reviewed before signature.</span></td></tr></tbody></table></div></div></div><div class="report-tab-content" data-tab-id="usage" hidden=""><div class="report-section" id="recommendation"><div class="report-section-header-column"><div class="report-section-subheader-row"><p class="report-section-title">Recommendation</p><p class="report-section-stat positive">10,400 licenses</p></div><div class="card-description"><p>Microsoft 365 renewal: how much to buy and at which edition. Prepared {{D+7}}, usage to {{D+5}}.</p></div></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">Basis</span></div><div class="card-description"><p>Based on usage to {{D+5}}; <strong>94% of users matched</strong> to the contract. The function owners&apos; confirmations of 3 and {{DS+6}} settle the quantity; nothing in it is still estimated.</p></div></div></div><div class="report-section-card-container"><p class="report-text">Renew <strong>10,400 licenses</strong>, down from 12,400: 8,300 E5 and 2,100 E3. At current prices that is $6.6M a year against $8.5M today, a saving of <strong>$1.9M</strong>; net after the vendor&apos;s expected response, <strong>$1.6M to $1.7M</strong>. The price lever adds $1.0M on top (see the playbook), for a target of $2.9M a year.</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Licenses next term</span><span class="report-stat-grid-value">10,400</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Saving a year</span><span class="report-stat-grid-value positive">$1.9M</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Days to notice</span><span class="report-stat-grid-value warning">55</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Owner confirmations</span><span class="report-stat-grid-value positive">5 of 5</span></div></div></div><div class="report-section-card-container"><div class="report-waterfall-wrap"><p class="report-table-title">From today&apos;s $8.5M to the $5.6M target: the quantity and edition levers are settled by the confirmations; the price lever is the negotiation&apos;s</p><svg class="report-waterfall" width="980" height="340" viewBox="0 0 980 340" xmlns="http://www.w3.org/2000/svg"><line x1="80" x2="955" y1="285" y2="285" class="report-waterfall-baseline"></line><text x="70" y="289" text-anchor="end" class="report-waterfall-tick">$0M</text><line x1="80" x2="955" y1="225" y2="225" class="report-waterfall-gridline"></line><text x="70" y="229" text-anchor="end" class="report-waterfall-tick">$2.5M</text><line x1="80" x2="955" y1="165" y2="165" class="report-waterfall-gridline"></line><text x="70" y="169" text-anchor="end" class="report-waterfall-tick">$5M</text><line x1="80" x2="955" y1="105" y2="105" class="report-waterfall-gridline"></line><text x="70" y="109" text-anchor="end" class="report-waterfall-tick">$7.5M</text><line x1="80" x2="955" y1="45" y2="45" class="report-waterfall-gridline"></line><text x="70" y="49" text-anchor="end" class="report-waterfall-tick">$10M</text><rect x="122.5" y="81" width="90" height="204" rx="4" class="report-waterfall-bar report-waterfall-bar-total"></rect><text x="167.5" y="72" text-anchor="middle" class="report-waterfall-value report-waterfall-value-total">$8,500,000</text><text x="167.5" y="303" text-anchor="middle" class="report-waterfall-label">Annual cost today</text><line x1="212.5" x2="297.5" y1="81" y2="81" class="report-waterfall-connector"></line><rect x="297.5" y="81" width="90" height="32.88000000000001" rx="4" class="report-waterfall-bar report-waterfall-bar-captured-saving"></rect><text x="342.5" y="72" text-anchor="middle" class="report-waterfall-value report-waterfall-value-captured-saving">−$1,370,000</text><text x="342.5" y="303" text-anchor="middle" class="report-waterfall-label">2,000 fewer licenses</text><line x1="387.5" x2="472.5" y1="113.88000000000001" y2="113.88000000000001" class="report-waterfall-connector"></line><rect x="472.5" y="113.88000000000001" width="90" height="12.719999999999985" rx="4" class="report-waterfall-bar report-waterfall-bar-captured-saving"></rect><text x="517.5" y="104.88000000000001" text-anchor="middle" class="report-waterfall-value report-waterfall-value-captured-saving">−$530,000</text><text x="517.5" y="303" text-anchor="middle" class="report-waterfall-label">2,100 users on E3</text><line x1="562.5" x2="647.5" y1="126.6" y2="126.6" class="report-waterfall-connector"></line><rect x="647.5" y="126.6" width="90" height="24" rx="4" class="report-waterfall-bar report-waterfall-bar-avoided-exposure"></rect><text x="692.5" y="117.6" text-anchor="middle" class="report-waterfall-value report-waterfall-value-avoided-exposure">−$1,000,000</text><text x="692.5" y="303" text-anchor="middle" class="report-waterfall-label">Price at the peer target</text><line x1="737.5" x2="822.5" y1="150.6" y2="150.6" class="report-waterfall-connector"></line><rect x="822.5" y="150.6" width="90" height="134.4" rx="4" class="report-waterfall-bar report-waterfall-bar-total"></rect><text x="867.5" y="141.6" text-anchor="middle" class="report-waterfall-value report-waterfall-value-total">$5,600,000</text><text x="867.5" y="303" text-anchor="middle" class="report-waterfall-label">Target annual cost</text></svg><div class="report-waterfall-legend"><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-total"></span>Total</span><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-captured-saving"></span>Captured Saving</span><span class="report-waterfall-legend-item"><span class="report-waterfall-legend-swatch report-waterfall-legend-swatch-avoided-exposure"></span>Avoided Exposure</span></div></div></div></div><div class="report-section" id="quantity"><div class="report-section-subheader-row"><p class="report-section-title">How the quantity was set</p></div><div class="report-section-card-container"><p class="report-text">From licenses bought to licenses needed: what was never assigned and what departed staff still hold goes now; what the function owners confirmed as not needed goes at renewal; the headcount plan adds back the hires, less the 210 kept seats that are reassigned to them. <strong>Nothing in the count is still under review.</strong></p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">From licenses bought to licenses needed</p><table class="report-table"><colgroup><col><col style="width: 20%"><col></colgroup><thead><tr><th><span>Step</span></th><th class="report-table-cell-numeric"><span>Licenses</span></th><th><span>Source</span></th></tr></thead><tbody><tr class="report-table-row-total"><td><span class="report-table-position">Bought</span></td><td class="report-table-cell-numeric"><span>12,400</span></td><td><span>Enterprise Agreement, {{D-48}} extraction</span></td></tr><tr><td><span class="report-table-position">Never assigned</span></td><td class="report-table-cell-numeric"><span>−550</span></td><td><span>M365 admin center, {{D-5}}</span></td></tr><tr><td><span class="report-table-position">Held by departed staff</span></td><td class="report-table-cell-numeric"><span>−560</span></td><td><span>Workday headcount, {{D-10}}</span></td></tr><tr><td><span class="report-table-position">Inactive and confirmed not needed by the owner</span></td><td class="report-table-cell-numeric"><span>−2,180</span></td><td><span>Owner confirmations: {{DS+4}} (Advisory, Technology, Corporate), {{DS+6}} (Operations, Client services)</span></td></tr><tr><td><span class="report-table-position">Planned hires, less the seats kept</span></td><td class="report-table-cell-numeric"><span>+1,290</span></td><td><span>Headcount plan to {{D+518}}: 1,500 hires, 210 of them on seats the owners keep</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Needed next term</span></td><td class="report-table-cell-numeric"><span>10,400</span></td><td><span>8,300 E5 and 2,100 E3</span></td></tr></tbody></table></div></div></div><div class="report-section" id="edition-mix"><div class="report-section-subheader-row"><p class="report-section-title">Edition mix</p><p class="report-section-stat">2,100 fit E3</p></div><div class="report-section-card-container"><p class="report-text"><strong>2,100 active users</strong> used none of the E5-only features in the 90 days to {{D+5}} (Defender and Purview, Teams Phone, audio conferencing, Power BI Pro, advanced eDiscovery). They fit E3 at $36 a user a month instead of $57. The remaining 6,800 used at least one E5-only feature.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">By function</p><table class="report-table"><thead><tr><th><span>Function</span></th><th class="report-table-cell-numeric"><span>Active users</span></th><th class="report-table-cell-numeric"><span>Fit E3</span></th><th class="report-table-cell-numeric"><span>Keep E5</span></th><th class="report-table-cell-numeric"><span>Inactive</span></th><th class="report-table-cell-numeric"><span>Released</span></th><th><span>Owner&apos;s confirmation</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Client services</span></td><td class="report-table-cell-numeric"><span>2,360</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>1,460</span></td><td class="report-table-cell-numeric"><span>510</span></td><td class="report-table-cell-numeric"><span>470</span></td><td><span class="positive">Dana Whitfield, {{DS+6}}: 40 kept for joiners not yet signed in</span></td></tr><tr><td><span class="report-table-position">Advisory and wealth</span></td><td class="report-table-cell-numeric"><span>2,050</span></td><td class="report-table-cell-numeric"><span>300</span></td><td class="report-table-cell-numeric"><span>1,750</span></td><td class="report-table-cell-numeric"><span>520</span></td><td class="report-table-cell-numeric"><span>520</span></td><td><span class="positive">Lena Graf, {{DS+4}}</span></td></tr><tr><td><span class="report-table-position">Technology</span></td><td class="report-table-cell-numeric"><span>2,300</span></td><td class="report-table-cell-numeric"><span>100</span></td><td class="report-table-cell-numeric"><span>2,200</span></td><td class="report-table-cell-numeric"><span>380</span></td><td class="report-table-cell-numeric"><span>380</span></td><td><span class="positive">Jonas Mills, {{DS+4}}</span></td></tr><tr><td><span class="report-table-position">Operations</span></td><td class="report-table-cell-numeric"><span>1,500</span></td><td class="report-table-cell-numeric"><span>600</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>640</span></td><td class="report-table-cell-numeric"><span>470</span></td><td><span class="positive">Marcus Oyelaran, {{DS+6}}: 170 kept for the seasonal claims team</span></td></tr><tr><td><span class="report-table-position">Corporate functions</span></td><td class="report-table-cell-numeric"><span>690</span></td><td class="report-table-cell-numeric"><span>200</span></td><td class="report-table-cell-numeric"><span>490</span></td><td class="report-table-cell-numeric"><span>340</span></td><td class="report-table-cell-numeric"><span>340</span></td><td><span class="positive">Adrian Meyer, {{DS+4}}</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>8,900</span></td><td class="report-table-cell-numeric"><span>2,100</span></td><td class="report-table-cell-numeric"><span>6,800</span></td><td class="report-table-cell-numeric"><span>2,390</span></td><td class="report-table-cell-numeric"><span>2,180</span></td><td><span></span></td></tr></tbody></table></div></div><div class="report-section-card-container"><p class="report-text">The 8,300 E5 is the 6,800 active E5 users, plus the 210 seats the owners keep, plus the 1,290 hires beyond them.</p></div></div><div class="report-section" id="range"><div class="report-section-subheader-row"><p class="report-section-title">What is kept</p></div><div class="report-section-card-container"><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">Operations keeps: the seasonal claims team, back in {{MON+73}}</span><span class="report-metric-list-value">170 seats</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Client services keeps: joiners not yet signed in</span><span class="report-metric-list-value">40 seats</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Reassigned to the hiring plan</span><span class="report-metric-list-value positive">210 seats</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Each 100 seats, at today&apos;s price</span><span class="report-metric-list-value">$68K a year</span></div></div></div><div class="report-section-card-container"><p class="report-text">The E3 count (2,100) does not move with the confirmations: those users are active, only their edition changes. The 210 kept seats go to new hires, which is why the headcount plan adds 1,290 licenses and not 1,500.</p></div></div><div class="report-section" id="asks"><div class="report-section-subheader-row"><p class="report-section-title">What to ask for</p></div><div class="report-section-card-container"><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">Reduce the quantity to 10,400 at renewal</span><span class="report-tag report-tag-negative">Quantity</span></div><div class="card-description"><p>8,300 E5 and 2,100 E3, settled by the owners&apos; confirmations; the reconciliation from bought to needed goes on the table as the order.</p></div></div><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">A right to reduce during the term</span><span class="report-tag report-tag-negative">Terms</span></div><div class="card-description"><p>The contract review found no right to reduce and no price protection. Ask for 10% a year without penalty.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">An E3 mix</span><span class="report-tag report-tag-warning">Edition</span></div><div class="card-description"><p>2,100 users at $36 instead of $57, with the right to move users between editions at the anniversary.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Price E5 at the peer target</span><span class="report-tag report-tag-warning">Price</span></div><div class="card-description"><p>$48.00 against $57.00 today; the peer median is $49.50 over 23 agreements.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Protection against the announced list rise</span><span class="report-tag report-tag-warning">Terms</span></div><div class="card-description"><p>Microsoft raises list prices 5% from {{D+153}}, the day after the renewal. Fix the price for the term.</p></div></div></div></div><div class="report-section" id="vendor-response"><div class="report-section-subheader-row"><p class="report-section-title">Vendor response and the net saving</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 18%"><col style="width: 22%"></colgroup><thead><tr><th><span>Lever</span></th><th class="report-table-cell-numeric"><span>Gross</span></th><th class="report-table-cell-numeric"><span>Net after response</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Right-size: quantity and edition</span></td><td class="report-table-cell-numeric"><span>$1.9M</span></td><td class="report-table-cell-numeric"><span>$1.6M to $1.7M</span></td></tr><tr><td><span class="report-table-position">Price: E5 at $48</span></td><td class="report-table-cell-numeric"><span>$1.1M</span></td><td class="report-table-cell-numeric"><span>$1.0M</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>$3.0M</span></td><td class="report-table-cell-numeric"><span class="positive">$2.5M to $2.7M</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-description-list"><span class="label">Expected vendor moves</span><div class="report-description-list-content"><div class="card-title"><span class="truncate">Re-pricing of the retained E5:</span></div><div class="card-description"><p>The per-user discount shrinks as the quantity falls; part of the right-size saving comes back as price.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Loss of a discount tier:</span></div><div class="card-description"><p>Below 10,000 E5 the agreement drops a tier; the E3 mix keeps the total above 10,000 licenses.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Bundling Copilot:</span></div><div class="card-description"><p>Expect an offer to fold Copilot into the renewal to hold the total; the 460 inactive Copilot seats say no.</p></div></div></div></div><div class="report-section-card-container"><p class="report-text">Figures are illustrative; the playbook carries the negotiation plan and the rounds.</p></div></div><div class="report-section" id="after-signing"><div class="report-section-subheader-row"><p class="report-section-title">After signing</p></div><div class="report-section-card-container"><p class="report-text">Check the first invoice, due {{D+243}}, against the signed quantities and unit prices.</p></div><div class="report-section-card-container"><p class="report-text">Re-run this analysis 90 days after the renewal date. The owner reviews become the baseline for the next term&apos;s reduction right.</p></div></div><div class="report-section" id="evidence"><div class="report-section-subheader-row"><p class="report-section-title">Evidence</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 30%"><col style="width: 20%"><col></colgroup><thead><tr><th><span>Source</span></th><th><span>As of</span></th><th><span>Supports</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Microsoft Entra sign-ins</span></td><td><span>{{D-1}}, daily</span></td><td><span>Last sign-in and days inactive per user</span></td></tr><tr><td><span class="report-table-position">Microsoft 365 admin center usage reports</span></td><td><span>{{D-5}}</span></td><td><span>Licenses assigned, E5 feature use per user (94% matched to the contract)</span></td></tr><tr><td><span class="report-table-position">Workday headcount</span></td><td><span>{{D-10}}</span></td><td><span>Employment status, departures, planned hires</span></td></tr><tr><td><span class="report-table-position">Coupa AP</span></td><td><span>{{D-6}}</span></td><td><span>Invoices paid; unit price $57 a user a month</span></td></tr><tr><td><span class="report-table-position">Enterprise Agreement extraction</span></td><td><span>{{D-48}}</span></td><td><span>Quantities, term, no right to reduce, no price protection</span></td></tr><tr><td><span class="report-table-position">Market data and playbook</span></td><td><span>{{D+7}}</span></td><td><span>Peer median $49.50 over 23 agreements; target $48.00</span></td></tr></tbody></table></div></div></div><div class="report-tab-content" data-tab-id="about" hidden=""><div class="report-section" id="deal"><div class="report-section-subheader-row"><p class="report-section-title">The deal today</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(6, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Licenses</span><span class="report-stat-grid-value">12,400</span><span class="report-stat-grid-sub">Microsoft 365 E5</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Unit price</span><span class="report-stat-grid-value negative">$57.00</span><span class="report-stat-grid-sub">A user a month; $684 a year</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Annual cost</span><span class="report-stat-grid-value">$8.5M</span><span class="report-stat-grid-sub">Plus $0.6M premier support</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Total contract value</span><span class="report-stat-grid-value">$25.5M</span><span class="report-stat-grid-sub">{{D-942}} to {{D+152}}</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Notice by</span><span class="report-stat-grid-value warning">{{D+62}}</span><span class="report-stat-grid-sub">90 days before the term ends</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Utilization</span><span class="report-stat-grid-value warning">72%</span><span class="report-stat-grid-sub">8,900 of 12,400 active in 90 days</span></div></div></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 28%"><col></colgroup><thead><tr><th><span>Item</span></th><th><span>Detail</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Supplier</span></td><td><span>Microsoft</span></td></tr><tr><td><span class="report-table-position">Agreement</span></td><td><span>Enterprise Agreement, Microsoft 365 E5 and add-ons</span></td></tr><tr><td><span class="report-table-position">Deal type</span></td><td><span>Renewal, year 3 of 3</span></td></tr><tr><td><span class="report-table-position">Term</span></td><td><span>{{D-942}} to {{D+152}}</span></td></tr><tr><td><span class="report-table-position">Reduction right</span></td><td><span>None: true-ups only during the term</span></td></tr><tr><td><span class="report-table-position">Price protection</span></td><td><span>None: a 5% list rise is announced from {{D+153}}</span></td></tr><tr><td><span class="report-table-position">Negotiation owner</span></td><td><span>Rachel Sato</span></td></tr></tbody></table></div></div><div class="report-section" id="benchmark"><div class="report-section-subheader-row"><p class="report-section-title">Market benchmark</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 30%"></colgroup><thead><tr><th><span>Peer position</span></th><th class="report-table-cell-numeric"><span>E5 a user a month</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Lower quartile</span></td><td class="report-table-cell-numeric"><span>$46.80</span></td></tr><tr><td><span class="report-table-position">Median</span></td><td class="report-table-cell-numeric"><span>$49.50</span></td></tr><tr><td><span class="report-table-position">Upper quartile</span></td><td class="report-table-cell-numeric"><span>$53.20</span></td></tr><tr><td><span class="report-table-position">You today</span></td><td class="report-table-cell-numeric"><span class="negative">$57.00</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Target</span></td><td class="report-table-cell-numeric"><span class="positive">$48.00</span></td></tr></tbody></table></div><div class="report-section-card-container"><p class="report-text">23 agreements of 8,000 to 15,000 E5 seats signed in the last 18 months, financial services and professional services.</p></div></div><div class="report-section" id="risks"><div class="report-section-subheader-row"><p class="report-section-title">Risks</p></div><div class="report-section-card-container"><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">Microsoft re-prices the retained E5</span><span class="report-tag report-tag-negative">High</span></div><div class="card-description"><p>If the price is held to recover the quantity, the net lands at $2.5M to $2.7M instead of $2.9M; the fallback position already allows for it.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Owners reopen their confirmations</span><span class="report-tag report-tag-warning">Medium</span></div><div class="card-description"><p>Operations and Client services kept 210 seats; a request to keep more after round 1 weakens the count. The confirmations are recorded on the project, with their dates.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Discount tier lost below 10,000 E5</span><span class="report-tag report-tag-warning">Medium</span></div><div class="card-description"><p>The E3 mix keeps the total above 10,000 licenses; confirm the tier counts E3.</p></div></div><div class="report-card banner positive"><div class="card-title-row"><span class="card-title positive">Copilot bundled to protect the total</span><span class="report-tag report-tag-positive">Low</span></div><div class="card-description"><p>460 of 1,000 Copilot seats are inactive; re-size to 600 in a separate order or decline the bundle.</p></div></div></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;
